import React, { useState, useRef, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Play, Pause, ChevronDown, ChevronLeft, ChevronRight, BookOpen, X as CloseIcon } from 'lucide-react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';

// ============= COLLECTIONS DATA =============
const collections = [
  {
    id: 'kokten-isiga',
    slug: 'kokten-isiga',
    number: '01',
    title: 'Kökten Işığa',
    subtitle: 'Güzelliği yüzeyde değil, özde arayanlar için',
    year: '2025',
    accent: '#8B7355',
    dark: '#2E2419',
    poster: 'https://customer-assets-cm19k8pv.emergentagent.net/job_3f3acde8-bbff-4f28-a1ca-d72be186d0f6/artifacts/yre7q9yc_KI.png',
    video: 'https://customer-assets.emergentagent.com/job_valmera-luxury/artifacts/b8m7yiyj_Bug%C3%BCn%20bir%20markan%C4%B1n%20do%C4%9Fu%C5%9Funu%20de%C4%9Fil%2C%20bir%20hayalin%20ger%C3%A7ekle%C5%9Fmesini%20izliyorsunuz.%20Bu%20s%C3%BCrecin%20nas%C4%B1l%20ad.mp4',
    gallery: [
      'https://customer-assets.emergentagent.com/job_silk-fular/artifacts/gfklpc2m_1.png',
      'https://customer-assets.emergentagent.com/job_silk-fular/artifacts/k3ryxhlq_4.1.png',
      'https://customer-assets.emergentagent.com/job_silk-fular/artifacts/v4v9kxl5_6.png',
    ],
    tagline: 'Ülkemizde sökülen zeytinlikleri gördük, içimiz acıdı.',
    story: [
      'Tasarımlarımızda hep duygularla başladık… Gördüğümüz, hissettiğimiz şeyleri anlatmanın bir yolunu aradık. "Kökten Işığa" da öyle doğdu aslında.',
      'Ülkemizde sökülen zeytinlikleri gördük, içimiz acıdı. O köklerin yerinden sökülmesi bize çok tanıdık geldi. Çünkü biz de bazen köklerimizden koparılıyoruz — özellikle kadınlar.',
      'Kadına verilen değerin günden güne azaldığını görmek bizi derinden etkiledi.'
    ],
    quote: 'O yüzden kadını ve zeytini aynı kökten düşündük. Biri toprağın bereketi, diğeri yaşamın özü.',
    chapters: [
      { title: 'BAŞLANGIÇ', image: 'https://customer-assets.emergentagent.com/job_silk-fular/artifacts/gfklpc2m_1.png', desc: 'İlk tasarımlarımızda sadece zeytin vardı… Çünkü hikâye orada başlıyordu.' },
      { title: 'GEÇİŞ', image: 'https://customer-assets.emergentagent.com/job_silk-fular/artifacts/k3ryxhlq_4.1.png', desc: 'Sonra kadın belirmeye başladı — zeytinle yan yana, eşit bir yerde.' },
      { title: 'IŞIK', image: 'https://customer-assets.emergentagent.com/job_silk-fular/artifacts/v4v9kxl5_6.png', desc: 'Zamanla kadın öne çıktı, tıpkı ışığın kökten doğması gibi.' },
    ],
    available: true,
    products: [
      { name: 'Bereket Kuşağı', sizes: ['65x65'], fabric: '%100 Has İpek', styles: ['Eşarp'] },
      { name: 'Zeytin Dalı', sizes: ['65x65', '90x90'], fabric: '%100 Has İpek', styles: ['Eşarp', 'Büstiyer'] },
      { name: 'Filiz Dinçmen', sizes: ['65x65', '90x90'], fabric: '%100 Has İpek', styles: ['Eşarp', 'Büstiyer'] },
      { name: 'Safiye Ali', sizes: ['65x65', '75x75', '90x90'], fabric: '%100 Has İpek', styles: ['Eşarp', 'Büstiyer'] },
      { name: 'Hâlide Edip Adıvar', sizes: ['65x65', '90x90'], fabric: '%100 Has İpek', styles: ['Eşarp', 'Büstiyer'] },
      { name: 'Afife Jale', sizes: ['65x65', '90x90'], fabric: '%100 Has İpek', styles: ['Eşarp', 'Büstiyer'] },
    ],
  },
  {
    id: 'turk-motifleri',
    slug: 'turk-motifleri',
    number: '02',
    title: 'Türk Motifleri',
    subtitle: 'Anadolu\'nun binlerce yıllık dokusu',
    year: '2026',
    accent: '#2E86AB',
    dark: '#0F2A44',
    poster: 'https://customer-assets-cm19k8pv.emergentagent.net/job_3f3acde8-bbff-4f28-a1ca-d72be186d0f6/artifacts/3uga6a0c_TM%20Afis%CC%A76.png',
    video: null,
    gallery: [
      'https://customer-assets-cm19k8pv.emergentagent.net/job_3f3acde8-bbff-4f28-a1ca-d72be186d0f6/artifacts/3uga6a0c_TM%20Afis%CC%A76.png',
      'https://customer-assets-cm19k8pv.emergentagent.net/job_3f3acde8-bbff-4f28-a1ca-d72be186d0f6/artifacts/dkok68qs_TM%20Afis%CC%A7%201.png',
      'https://customer-assets-cm19k8pv.emergentagent.net/job_3f3acde8-bbff-4f28-a1ca-d72be186d0f6/artifacts/p730pe83_tm%20aifs%CC%A7.png',
    ],
    tagline: 'BLAUNDUS × VALMÉRA — Antik topraklarda dokunan yeni bir hikâye.',
    story: [
      'Türk Motifleri koleksiyonu, Anadolu\'nun bin yıllık kültürel mirasını çağdaş bir dille yeniden yorumluyor. Selçuklu\'nun geometrisi, Osmanlı\'nın çiçeği, İznik\'in mavisi… hepsi tek bir ipek yüzeyinde buluşuyor.',
      'İlhamımız, batı Anadolu\'nun sessiz antik kenti Blaundus\'tan geliyor. Sütunları arasında yankılanan sükûnet, taşına işlenen motifler ve zamanın rüzgârıyla soluklaşan renkler bu koleksiyonun karakterini belirledi.',
      'Twill dokulu %100 has ipeğin üzerine düşen her motif, geçmişin taşlarından bugünün kadınına uzanan bir mektup gibi.'
    ],
    quote: 'Kökler unutulmaz. Sadece yeni ışıklarla yeniden görünür olur.',
    chapters: [
      { title: 'ANTİK TAŞ', image: 'https://customer-assets-cm19k8pv.emergentagent.net/job_3f3acde8-bbff-4f28-a1ca-d72be186d0f6/artifacts/3uga6a0c_TM%20Afis%CC%A76.png', desc: 'Blaundus\'un turkuaz gökyüzünün altında, cesaret ve kadim güç bir ipeğin üstünde buluştu.' },
      { title: 'YILDIZ SARAYI', image: 'https://customer-assets-cm19k8pv.emergentagent.net/job_3f3acde8-bbff-4f28-a1ca-d72be186d0f6/artifacts/dkok68qs_TM%20Afis%CC%A7%201.png', desc: 'Osmanlı yıldızının bordo tonlarında saklı bir sarayı yeniden hayal ettik.' },
      { title: 'İZNİK ÇİÇEĞİ', image: 'https://customer-assets-cm19k8pv.emergentagent.net/job_3f3acde8-bbff-4f28-a1ca-d72be186d0f6/artifacts/p730pe83_tm%20aifs%CC%A7.png', desc: 'Çinilerin çiçek dilinin, ipeğin üstünde soluklanan fısıltısı.' },
    ],
    available: true,
    products: [
      { name: 'Umay', sizes: ['105x105'], fabric: 'Twill İpek · %100 İpek', styles: ['Eşarp'] },
      { name: 'Yıldız Sarayı', sizes: ['90x90'], fabric: 'Twill İpek · %100 İpek', styles: ['Eşarp', 'Büstiyer'] },
      { name: 'Bereket', sizes: ['90x90'], fabric: 'Twill İpek · %100 İpek', styles: ['Eşarp', 'Büstiyer'] },
      { name: 'İznik Baharı', sizes: ['90x90'], fabric: 'Twill İpek · %100 İpek', styles: ['Eşarp', 'Büstiyer'] },
      { name: 'Anadolu Nakşı', sizes: ['90x90'], fabric: 'Twill İpek · %100 İpek', styles: ['Eşarp', 'Büstiyer'] },
      { name: 'Ahenk', sizes: ['65x65'], fabric: 'Twill İpek · %100 İpek', styles: ['Eşarp'] },
    ],
  },
  {
    id: 'yaz-masali',
    slug: 'yaz-masali',
    number: '03',
    title: 'Yaz Masalı',
    subtitle: 'Güneşin ipeğe düştüğü an',
    year: '2026',
    accent: '#E4A853',
    dark: '#4A2E14',
    poster: 'https://customer-assets-cm19k8pv.emergentagent.net/job_3f3acde8-bbff-4f28-a1ca-d72be186d0f6/artifacts/n60bnjxu_1.webp',
    video: null,
    gallery: [],
    tagline: 'Yaz\'ın en ışıltılı hikâyesi, ipekle yazılıyor.',
    story: [
      'Yaz Masalı; sıcak günlerin, deniz kıyısında savrulan saçların ve gün batımının renklerini taşıyan bir koleksiyon.',
      'Zambak\'ın beyazı, hibiskusun ateşi, sarayın esintisi… Hafif viskon dokusu, tenle konuşan bir sadelikte tasarlandı.',
      'Yazın hafızasına düşülen bir not gibi; savurmalı, uçmalı, ışığı yakalamalı.'
    ],
    quote: 'İpek gibi hafif, güneş gibi sıcak.',
    chapters: [],
    available: true,
    products: [
      { name: 'Beyaz Zambak', sizes: ['140x140'], fabric: '%100 Viskon', styles: ['Şal / Pareo'] },
      { name: 'Hibiskus', sizes: ['140x140'], fabric: '%100 Viskon', styles: ['Şal / Pareo'] },
      { name: 'Saray Esintisi', sizes: ['140x140'], fabric: '%100 Viskon', styles: ['Şal / Pareo'] },
    ],
  },
  {
    id: 'sakli-bahce',
    slug: 'sakli-bahce',
    number: '04',
    title: 'Saklı Bahçe',
    subtitle: 'Yalnız sana açılan bir sır',
    year: '2026',
    accent: '#4A7C59',
    dark: '#1A2E20',
    poster: 'https://customer-assets-cm19k8pv.emergentagent.net/job_3f3acde8-bbff-4f28-a1ca-d72be186d0f6/artifacts/9uhugh6k_Sakl%C4%B1%20Bahc%CC%A7e%20.png',
    video: null,
    gallery: [],
    tagline: 'Doğanın sessiz zarafeti — mahrem bir güzelliğin ipektaki hâli.',
    story: [
      'Saklı Bahçe, yalnız yakından bakıldığında görülen çiçek ve yaprakların koleksiyonu. Açelya\'nın al kırmızısı, nergisin sarı ışığı, yıldız çiçeğinin geometrisi, yabani gülün asaletli inatçılığı.',
      'Botanik illüstrasyonlar ve fısıltı halinde renklerle, her fular bir bahçenin en gizli köşesini ele veriyor.',
      'Ne kimseye anlatılan, ne kimseden saklanan — sadece kendine ait olan bir güzellik.'
    ],
    quote: 'En güzel çiçekler, en az görülenlerdir.',
    chapters: [],
    available: true,
    products: [
      { name: 'Açelya', sizes: ['90x90'], fabric: '%100 İpek', styles: ['Eşarp', 'Büstiyer'] },
      { name: 'Nergis', sizes: ['90x90'], fabric: '%100 İpek', styles: ['Eşarp', 'Büstiyer'] },
      { name: 'Yıldız Çiçeği', sizes: ['90x90'], fabric: '%100 İpek', styles: ['Eşarp', 'Büstiyer'] },
      { name: 'Yabani Gül', sizes: ['90x90'], fabric: '%100 İpek', styles: ['Eşarp', 'Büstiyer'] },
    ],
  },
  {
    id: 'goc',
    slug: 'goc',
    number: '05',
    title: 'Göç',
    subtitle: 'Bir yerden diğerine, bir kalpten diğerine',
    year: '2026',
    accent: '#8B4A6B',
    dark: '#2A1421',
    poster: 'https://customer-assets-cm19k8pv.emergentagent.net/job_3f3acde8-bbff-4f28-a1ca-d72be186d0f6/artifacts/1mbqa4fp_Go%CC%88c%CC%A7%20Afis%CC%A7.png',
    video: null,
    gallery: [],
    tagline: 'Kuşların, mevsimlerin ve insanların yol hikâyeleri — saten ipeğe işlendi.',
    story: [
      'Göç koleksiyonu; kuşların gökyüzündeki yolculuğunu, mevsimlerin sessiz değişimini ve insanın yerinden edilen kalbini anlatıyor.',
      'Nilüfer\'in sudaki yolculuğu, kırlangıcın uzun rotası, tavus kuşunun gösterişli duruşu ve sınır ötesi bir umut — hepsi saten ipeğin parıltısında birleşiyor.',
      'Her fular, bir hikâyenin taşındığı bir yol; her düğüm, bir hatıranın demirlendiği bir liman.'
    ],
    quote: 'Göç, bir gitmek değil; bir başka yerde yeniden var olmaktır.',
    chapters: [],
    available: true,
    products: [
      { name: 'Nilüfer', sizes: ['65x65'], fabric: 'Saten İpek · %100 İpek', styles: ['Eşarp'] },
      { name: 'Kırlangıç & Puantiye', sizes: ['65x65'], fabric: 'Saten İpek · %100 İpek', styles: ['Eşarp'] },
    ],
  },
  {
    id: 'nazende',
    slug: 'nazende',
    number: '06',
    title: 'Nazende',
    subtitle: 'Tek bir düğümde saklı, uzun bir hikâye',
    year: '2026',
    accent: '#B08D57',
    dark: '#2B1E12',
    poster: null,
    video: null,
    gallery: [],
    tagline: 'Uzun bir şal, kısa bir masal — Nazende.',
    story: [
      'Nazende, boyun etrafına dolanan ince ve uzun bir şalın adı. 69x200 cm crep ipek üzerine, akıp giden bir el yazısı gibi tasarlandı.',
      'Tek başına eksiksiz, iki ucundan tutulduğunda bir yolun başlangıcı.',
      'Yalnızca tek bir tasarımla bir koleksiyon oluşturur — çünkü bazı hikâyeler tek başlarına anlatılır.'
    ],
    quote: 'Nazende — bir düğümde yaşayan zarafet.',
    chapters: [],
    available: true,
    products: [
      { name: 'Nazende Şal', sizes: ['69x200'], fabric: 'Crep İpek · %100 İpek', styles: ['Şal'] },
      { name: 'Nazende Büstiyer', sizes: ['69x200'], fabric: 'Crep İpek · %100 İpek', styles: ['Büstiyer'] },
    ],
  },
];

// ============= HERO AUTO SLIDER (Ken Burns reel for modal top) =============
const HeroAutoSlider = ({ chapters, duration = 6000 }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => {
      setIndex((prev) => (prev + 1) % chapters.length);
    }, duration);
    return () => clearTimeout(t);
  }, [index, chapters.length, duration]);

  return (
    <div className="absolute inset-0 overflow-hidden">
      <AnimatePresence mode="wait">
        <motion.div
          key={index}
          initial={{ opacity: 0, scale: 1.15 }}
          animate={{
            opacity: 1,
            scale: [1.15, 1.28, 1.15],
            x: [0, index % 2 === 0 ? -30 : 30, 0],
          }}
          exit={{ opacity: 0 }}
          transition={{
            opacity: { duration: 1.2 },
            scale: { duration: duration / 1000, ease: 'easeInOut' },
            x: { duration: duration / 1000, ease: 'easeInOut' },
          }}
          className="absolute inset-0"
        >
          <img
            src={chapters[index].image}
            alt={chapters[index].title}
            className="w-full h-full object-cover"
          />
        </motion.div>
      </AnimatePresence>

      {/* Slide dots + timeline (top) */}
      <div className="absolute top-24 md:top-28 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
        {chapters.map((_, i) => (
          <button
            key={i}
            onClick={() => setIndex(i)}
            className="relative overflow-hidden"
            aria-label={`Sahne ${i + 1}`}
            data-testid={`hero-slider-dot-${i}`}
          >
            <div className={`transition-all ${i === index ? 'w-14 h-0.5 bg-white/30' : 'w-3 h-0.5 bg-white/30 hover:bg-white/60'}`}>
              {i === index && (
                <motion.div
                  key={`bar-${i}-${index}`}
                  initial={{ width: 0 }}
                  animate={{ width: '100%' }}
                  transition={{ duration: duration / 1000, ease: 'linear' }}
                  className="h-0.5 bg-white"
                />
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Sub label */}
      <div className="absolute top-16 md:top-20 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
        <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
        <span className="text-white/80 text-[10px] tracking-[0.4em]">
          CANLI · SAHNE {String(index + 1).padStart(2, '0')} / {String(chapters.length).padStart(2, '0')} · {chapters[index].title}
        </span>
      </div>
    </div>
  );
};
// ============= HERO AUTO SLIDER END =============


// ============= CINEMATIC IMAGE VIEWER (Ken Burns "video" effect) =============
const CinematicImageViewer = ({ chapters, startIndex = 0, accent = '#fff', onClose }) => {
  const [index, setIndex] = useState(startIndex);
  const [direction, setDirection] = useState(1);
  const chapter = chapters[index];

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowRight' && index < chapters.length - 1) { setDirection(1); setIndex(index + 1); }
      else if (e.key === 'ArrowLeft' && index > 0) { setDirection(-1); setIndex(index - 1); }
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line
  }, [index]);

  // Auto-advance timer (like a video)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (index < chapters.length - 1) {
        setDirection(1);
        setIndex(index + 1);
      }
    }, 8000);
    return () => clearTimeout(timer);
  }, [index, chapters.length]);

  if (!chapter) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.6 }}
        className="fixed inset-0 z-[300] bg-black"
        data-testid="cinematic-viewer"
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 z-[320] w-12 h-12 border border-white/40 text-white flex items-center justify-center hover:bg-white hover:text-black transition-all"
          aria-label="Kapat"
          data-testid="close-cinematic"
        >
          <CloseIcon size={16} />
        </button>

        {/* Ken Burns image */}
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 1.15, x: direction > 0 ? 50 : -50 }}
            animate={{
              opacity: 1,
              scale: [1.15, 1.28, 1.15],
              x: [0, direction > 0 ? -40 : 40, 0],
              y: [0, -20, 0],
            }}
            exit={{ opacity: 0 }}
            transition={{
              opacity: { duration: 1.2 },
              x: { duration: 8, ease: 'easeInOut' },
              y: { duration: 8, ease: 'easeInOut' },
              scale: { duration: 8, ease: 'easeInOut' },
            }}
            className="absolute inset-0"
          >
            <img
              src={chapter.image}
              alt={chapter.title}
              className="w-full h-full object-cover"
            />
            {/* Cinematic bars (letterbox) */}
            <div className="absolute inset-x-0 top-0 h-[8%] bg-black" />
            <div className="absolute inset-x-0 bottom-0 h-[8%] bg-black" />
            {/* Vignette */}
            <div className="absolute inset-0 pointer-events-none" style={{
              background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.7) 100%)'
            }} />
          </motion.div>
        </AnimatePresence>

        {/* Overlay content */}
        <div className="absolute inset-x-0 bottom-[10%] px-8 md:px-20 pointer-events-none">
          <motion.div
            key={`text-${index}`}
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: accent }} />
              <span className="text-white/80 text-[10px] tracking-[0.4em]">
                CANLI · SAHNE {String(index + 1).padStart(2, '0')} / {String(chapters.length).padStart(2, '0')}
              </span>
            </div>
            <h2 className="font-serif text-5xl md:text-7xl font-light text-white leading-tight mb-4">
              {chapter.title}
            </h2>
            <p className="text-white/80 text-lg md:text-xl font-light max-w-2xl">
              {chapter.desc}
            </p>
          </motion.div>
        </div>

        {/* Nav */}
        <button
          onClick={() => { if (index > 0) { setDirection(-1); setIndex(index - 1); } }}
          disabled={index === 0}
          className={`absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-[310] w-14 h-14 rounded-full border border-white/30 text-white flex items-center justify-center transition-all ${index === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-white hover:text-black'}`}
          data-testid="cinematic-prev"
        >
          <ChevronLeft size={22} />
        </button>
        <button
          onClick={() => { if (index < chapters.length - 1) { setDirection(1); setIndex(index + 1); } }}
          disabled={index === chapters.length - 1}
          className={`absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-[310] w-14 h-14 rounded-full border border-white/30 text-white flex items-center justify-center transition-all ${index === chapters.length - 1 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-white hover:text-black'}`}
          data-testid="cinematic-next"
        >
          <ChevronRight size={22} />
        </button>

        {/* Progress dots + timeline */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-[310]">
          {chapters.map((_, i) => (
            <button
              key={i}
              onClick={() => { setDirection(i > index ? 1 : -1); setIndex(i); }}
              className="relative overflow-hidden"
              data-testid={`cinematic-dot-${i}`}
              aria-label={`Sahne ${i + 1}`}
            >
              <div className={`transition-all ${i === index ? 'w-16 h-0.5 bg-white/30' : 'w-3 h-0.5 bg-white/30 hover:bg-white/60'}`}>
                {i === index && (
                  <motion.div
                    key={`bar-${i}-${index}`}
                    initial={{ width: 0 }}
                    animate={{ width: '100%' }}
                    transition={{ duration: 8, ease: 'linear' }}
                    className="h-0.5 bg-white"
                  />
                )}
              </div>
            </button>
          ))}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// ============= CINEMATIC IMAGE VIEWER END =============

// ============= VINTAGE FLIPBOOK CATALOG =============
// Dekoratif alt bileşenler (component dışında — re-render performansı)
const FiligreeCorner = ({ color, size = 54 }) => (
  <svg
    width={size} height={size} viewBox="0 0 64 64"
    style={{ color }}
    aria-hidden
  >
    <g fill="none" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round">
      <path d="M6,6 L30,6 M6,6 L6,30" opacity="0.9" />
      <path d="M6,6 Q18,10 24,18 Q22,22 20,26 Q18,30 14,32" opacity="0.8" />
      <path d="M12,12 Q22,16 26,24" opacity="0.6" />
      <path d="M10,14 Q14,20 20,22" opacity="0.5" />
      <circle cx="22" cy="22" r="1.2" fill="currentColor" opacity="0.9" />
      <circle cx="16" cy="30" r="0.9" fill="currentColor" opacity="0.7" />
      <path d="M6,6 L10,10 M14,6 L6,14" opacity="0.5" strokeWidth="0.5" />
      <path d="M26,10 Q32,8 36,14 Q38,20 32,22 Q28,20 26,16 Z" fill="currentColor" opacity="0.25" />
      <path d="M10,26 Q8,32 14,36 Q20,38 22,32 Q20,28 16,26 Z" fill="currentColor" opacity="0.2" />
    </g>
  </svg>
);

const OrnamentDivider = ({ color, width = 240 }) => (
  <svg width={width} height="18" viewBox="0 0 240 18" style={{ color }} aria-hidden>
    <g fill="none" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round">
      <line x1="4" y1="9" x2="90" y2="9" opacity="0.5" />
      <line x1="150" y1="9" x2="236" y2="9" opacity="0.5" />
      <path d="M90,9 Q100,2 112,9 Q120,16 128,9 Q140,2 150,9" opacity="0.9" />
      <path d="M118,5 Q120,2 122,5 Q122,8 120,9 Q118,8 118,5 Z" fill="currentColor" opacity="0.6" />
      <circle cx="120" cy="9" r="1.4" fill="currentColor" />
      <circle cx="104" cy="9" r="0.6" fill="currentColor" opacity="0.5" />
      <circle cx="136" cy="9" r="0.6" fill="currentColor" opacity="0.5" />
    </g>
  </svg>
);

const VintageCatalogBook = ({ collection, startIndex = 0, onClose }) => {
  // Build flat pages: for each product, one page per image + one features page
  const buildPages = () => {
    const pages = [];
    collection.products.forEach((p, prodIdx) => {
      const images = (p.images && p.images.length > 0) ? p.images : (p.image ? [p.image] : []);
      if (images.length > 0) {
        images.forEach((img, imgIdx) => {
          pages.push({ type: 'image', product: p, prodIdx, image: img, imageIdx: imgIdx, totalImages: images.length });
        });
      } else {
        pages.push({ type: 'image', product: p, prodIdx, image: null, imageIdx: 0, totalImages: 0 });
      }
      pages.push({ type: 'details', product: p, prodIdx });
    });
    return pages;
  };

  const pages = buildPages();
  const totalPages = pages.length;

  // Find first page for startIndex (which is a product index)
  const findStartPage = () => {
    for (let i = 0; i < pages.length; i++) {
      if (pages[i].prodIdx === startIndex && pages[i].type === 'image') return i;
    }
    return 0;
  };

  const [page, setPage] = useState(findStartPage());
  const [direction, setDirection] = useState(0);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const next = () => {
    if (page < totalPages - 1) { setDirection(1); setPage(page + 1); }
  };
  const prev = () => {
    if (page > 0) { setDirection(-1); setPage(page - 1); }
  };

  const currentPage = pages[page];
  if (!currentPage) return null;

  // === Vintage sepia palette ===
  const paperBg = '#efe0bd';        // Yıpranmış fildişi
  const paperDark = '#e8d6a7';      // Yaşlanmış kağıt
  const inkColor = '#2b1810';       // Antika siyah mürekkep
  const accentInk = '#8b6230';      // Altın sepia
  const leather = '#2a1810';        // Deri cilt
  const leatherAccent = '#3f2816';  // Deri kabartma
  const gilt = '#c8a24a';           // Altın yaldız

  const product = currentPage.product;
  const productImages = product.images || (product.image ? [product.image] : []);
  const rightImg = productImages[currentPage.imageIdx + 1] || productImages[0];

  // Formatted price (min)
  const formatPrice = (p) =>
    typeof p === 'number' ? p.toLocaleString('tr-TR') + ' ₺' : p;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.6 }}
        className="fixed inset-0 z-[200] flex items-center justify-center px-4 py-6"
        style={{
          background:
            'radial-gradient(ellipse at center, #1a0f08 0%, #060402 70%), repeating-linear-gradient(45deg, rgba(255,220,160,0.015) 0 2px, transparent 2px 20px)',
        }}
        data-testid="catalog-book-modal"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 z-[220] w-11 h-11 rounded-full border border-amber-200/30 text-amber-100/80 flex items-center justify-center hover:bg-amber-100 hover:text-black transition-all backdrop-blur-sm"
          data-testid="close-catalog-book"
          aria-label="Kapat"
        >
          <CloseIcon size={16} />
        </button>

        {/* Header hint */}
        <div className="absolute top-6 left-1/2 -translate-x-1/2 text-amber-100/50 text-[10px] md:text-xs tracking-[0.5em] flex items-center gap-3 select-none z-[210]">
          <BookOpen size={13} />
          <span className="hidden md:inline">
            {collection.title.toUpperCase()}
            <span className="mx-3 opacity-50">◆</span>
            VALMÉRA KATALOGU
          </span>
          <span className="md:hidden">KATALOG</span>
        </div>

        {/* ============ KİTAP (dış cilt + sayfalar) ============ */}
        <div
          className="relative w-full max-w-[1180px]"
          style={{ perspective: '2400px' }}
        >
          {/* Alt gölge */}
          <div
            className="absolute -inset-x-10 -bottom-6 h-10 rounded-full blur-2xl opacity-80 pointer-events-none"
            style={{ background: '#000' }}
          />

          {/* === DIŞ DERİ CİLT (outer leather binding) === */}
          <div
            className="relative mx-auto rounded-md"
            style={{
              padding: '26px 22px 28px 22px',
              background: `
                linear-gradient(135deg, ${leatherAccent} 0%, ${leather} 50%, #140a04 100%)
              `,
              boxShadow: `
                0 36px 72px -14px rgba(0,0,0,0.9),
                inset 0 0 60px rgba(0,0,0,0.6),
                inset 0 1px 0 rgba(255,220,160,0.08)
              `,
              border: `1px solid ${gilt}22`,
            }}
          >
            {/* Deri doku */}
            <div
              className="absolute inset-0 opacity-45 mix-blend-overlay pointer-events-none rounded-md"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 300'%3E%3Cfilter id='l'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='2' numOctaves='2'/%3E%3CfeColorMatrix values='0 0 0 0 0.17  0 0 0 0 0.1  0 0 0 0 0.05  0 0 0 0.9 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23l)'/%3E%3C/svg%3E")`,
              }}
            />

            {/* Deri cilt köşelerinde altın pirinç — book corner metal fittings */}
            {[
              { pos: 'top-2 left-2', rot: '0deg' },
              { pos: 'top-2 right-2', rot: '90deg' },
              { pos: 'bottom-2 right-2', rot: '180deg' },
              { pos: 'bottom-2 left-2', rot: '270deg' },
            ].map((corner, i) => (
              <div
                key={i}
                className={`absolute ${corner.pos} w-6 h-6 pointer-events-none`}
                style={{ transform: `rotate(${corner.rot})` }}
              >
                <svg viewBox="0 0 24 24" width="24" height="24" style={{ color: gilt }}>
                  <path
                    d="M2,2 L18,2 L22,6 L22,22 L2,22 Z"
                    fill="currentColor"
                    opacity="0.75"
                  />
                  <path
                    d="M4,4 L16,4 L20,8 L20,20 L4,20 Z"
                    fill="#5a3812"
                    opacity="0.7"
                  />
                  <circle cx="8" cy="8" r="1" fill="currentColor" opacity="0.9" />
                </svg>
              </div>
            ))}

            {/* === SAYFALAR + CİLT SIRTI === */}
            <div
              className="relative w-full flex overflow-hidden shadow-[inset_0_0_80px_rgba(0,0,0,0.4)] rounded-sm"
              style={{
                aspectRatio: '16 / 9.6',
                background: '#000',
              }}
            >
              {/* ========== SOL SAYFA ========== */}
              <div
                className="relative flex-1 h-full overflow-hidden"
                style={{
                  background: `
                    radial-gradient(ellipse at 70% 50%, ${paperBg} 0%, ${paperDark} 100%)
                  `,
                }}
              >
                {/* Kağıt dokusu */}
                <div
                  className="absolute inset-0 opacity-55 mix-blend-multiply pointer-events-none"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.4' numOctaves='3'/%3E%3CfeColorMatrix values='0 0 0 0 0.5  0 0 0 0 0.4  0 0 0 0 0.25  0 0 0 0.5 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
                  }}
                />

                {/* Yaşlanma lekeleri (foxing) */}
                <div
                  className="absolute inset-0 opacity-30 pointer-events-none mix-blend-multiply"
                  style={{
                    backgroundImage: `
                      radial-gradient(circle at 15% 20%, rgba(130,80,30,0.35), transparent 8%),
                      radial-gradient(circle at 85% 70%, rgba(130,80,30,0.25), transparent 6%),
                      radial-gradient(circle at 30% 85%, rgba(130,80,30,0.3), transparent 5%),
                      radial-gradient(circle at 70% 15%, rgba(130,80,30,0.2), transparent 4%)
                    `,
                  }}
                />

                {/* Yıpranmış köşe gölgeleri */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: `
                      radial-gradient(circle at 0% 0%, rgba(122,88,50,0.5), transparent 25%),
                      radial-gradient(circle at 0% 100%, rgba(122,88,50,0.55), transparent 22%),
                      radial-gradient(circle at 100% 0%, rgba(122,88,50,0.2), transparent 18%),
                      radial-gradient(circle at 100% 100%, rgba(122,88,50,0.3), transparent 20%)
                    `,
                  }}
                />

                {/* Çift ince altın çerçeve */}
                <div className="absolute inset-5 pointer-events-none" style={{ border: `0.5px solid ${accentInk}55` }} />
                <div className="absolute inset-[18px] pointer-events-none" style={{ border: `0.5px solid ${accentInk}33` }} />

                {/* === Filigran köşe süslemeleri === */}
                <div className="absolute top-3 left-3 pointer-events-none">
                  <FiligreeCorner color={accentInk} />
                </div>
                <div className="absolute top-3 right-3 pointer-events-none" style={{ transform: 'scaleX(-1)' }}>
                  <FiligreeCorner color={accentInk} />
                </div>
                <div className="absolute bottom-3 left-3 pointer-events-none" style={{ transform: 'scaleY(-1)' }}>
                  <FiligreeCorner color={accentInk} />
                </div>
                <div className="absolute bottom-3 right-3 pointer-events-none" style={{ transform: 'scale(-1,-1)' }}>
                  <FiligreeCorner color={accentInk} />
                </div>

                {/* Üst dekoratif bant + koleksiyon adı */}
                <div
                  className="absolute top-9 left-0 right-0 flex flex-col items-center pointer-events-none z-[2]"
                  style={{ color: accentInk }}
                >
                  <OrnamentDivider color={accentInk} />
                  <div className="font-serif italic text-[10px] md:text-[11px] tracking-[0.5em] mt-1">
                    {collection.title.toUpperCase()}
                  </div>
                </div>

                {/* === İÇERİK === */}
                <AnimatePresence mode="wait" custom={direction}>
                  <motion.div
                    key={page + '-L'}
                    initial={{ rotateY: direction > 0 ? -70 : 70, opacity: 0, filter: 'brightness(0.5)' }}
                    animate={{ rotateY: 0, opacity: 1, filter: 'brightness(1)' }}
                    exit={{ rotateY: direction > 0 ? 70 : -70, opacity: 0, filter: 'brightness(0.5)' }}
                    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    style={{ transformOrigin: 'right center', transformStyle: 'preserve-3d' }}
                    className="absolute inset-0 flex items-center justify-center p-14 md:p-16"
                  >
                    {currentPage.image ? (
                      /* === Ürün görseli (illüstrasyon çerçeveli) === */
                      <div className="relative w-full h-full flex items-center justify-center">
                        <div
                          className="relative w-[86%] h-[82%] overflow-hidden"
                          style={{
                            boxShadow: `
                              0 8px 30px -8px rgba(0,0,0,0.45),
                              inset 0 0 0 1px ${accentInk}44,
                              inset 0 0 0 6px ${paperBg},
                              inset 0 0 0 7px ${accentInk}66
                            `,
                            border: `1px solid ${accentInk}aa`,
                            padding: '8px',
                            background: paperBg,
                          }}
                        >
                          <div className="w-full h-full overflow-hidden">
                            <img
                              src={currentPage.image}
                              alt={product.name}
                              className="w-full h-full object-cover"
                              style={{
                                filter: 'sepia(0.12) saturate(0.95) contrast(1.02)',
                              }}
                            />
                          </div>
                        </div>

                        {/* El yazısı caption */}
                        <div className="absolute bottom-6 left-0 right-0 text-center">
                          <div
                            className="font-serif italic text-[11px] md:text-[12px]"
                            style={{ color: inkColor, letterSpacing: '0.1em' }}
                          >
                            — {product.name} —
                          </div>
                          <div
                            className="text-[9px] tracking-[0.35em] mt-1"
                            style={{ color: accentInk, opacity: 0.75 }}
                          >
                            Planche № {String(currentPage.imageIdx + 1).padStart(2, '0')} / {String(currentPage.totalImages).padStart(2, '0')}
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* === Ürün görseli yoksa ornamental cover === */
                      <div className="relative w-full h-full flex items-center justify-center">
                        <svg viewBox="0 0 400 500" className="absolute inset-0 w-full h-full" style={{ color: accentInk }}>
                          <rect x="30" y="30" width="340" height="440" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.5" />
                          <g transform="translate(200,250)" opacity="0.5">
                            <circle r="90" fill="none" stroke="currentColor" strokeWidth="0.8" />
                            <circle r="60" fill="none" stroke="currentColor" strokeWidth="0.5" />
                            <path d="M0,-90 Q25,-45 0,0 Q-25,-45 0,-90 M0,90 Q25,45 0,0 Q-25,45 0,90 M-90,0 Q-45,25 0,0 Q-45,-25 -90,0 M90,0 Q45,25 0,0 Q45,-25 90,0" fill="currentColor" opacity="0.5" />
                            <circle r="10" fill="currentColor" />
                          </g>
                        </svg>
                        <div
                          className="relative z-10 font-serif italic leading-none"
                          style={{
                            color: inkColor,
                            fontSize: '140px',
                            textShadow: `2px 2px 0 ${accentInk}22`,
                          }}
                        >
                          {product.name[0]}
                        </div>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>

                {/* Sayfa katlama gölgesi (sağ kenar - cilt sırtı yakını) */}
                <div
                  className="absolute inset-y-0 right-0 w-16 pointer-events-none"
                  style={{
                    background:
                      'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.08) 40%, rgba(0,0,0,0.4) 100%)',
                  }}
                />

                {/* Sol sayfa numarası (dekoratif) */}
                <div
                  className="absolute bottom-6 left-0 right-0 flex items-center justify-center pointer-events-none"
                  style={{ color: accentInk }}
                >
                  <div className="flex items-center gap-3">
                    <svg width="16" height="6" viewBox="0 0 16 6" style={{ color: accentInk, opacity: 0.6 }}>
                      <path d="M1,3 L15,3 M3,3 Q4,1 5,3 M11,3 Q12,1 13,3" stroke="currentColor" strokeWidth="0.6" fill="none" />
                    </svg>
                    <span className="font-serif italic text-[10px] tracking-[0.35em]">
                      — {page * 2 + 1} —
                    </span>
                    <svg width="16" height="6" viewBox="0 0 16 6" style={{ color: accentInk, opacity: 0.6 }}>
                      <path d="M1,3 L15,3 M3,3 Q4,1 5,3 M11,3 Q12,1 13,3" stroke="currentColor" strokeWidth="0.6" fill="none" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* ========== CİLT SIRTI (merkez) ========== */}
              <div
                className="relative w-3 md:w-5 h-full"
                style={{
                  background: `
                    linear-gradient(to right,
                      rgba(0,0,0,0.9) 0%,
                      rgba(0,0,0,0.3) 15%,
                      ${leather} 50%,
                      rgba(0,0,0,0.3) 85%,
                      rgba(0,0,0,0.9) 100%)
                  `,
                  boxShadow: 'inset 0 0 10px rgba(0,0,0,0.9)',
                }}
              >
                {/* Dikiş noktaları */}
                <div className="absolute inset-y-6 left-1/2 -translate-x-1/2 flex flex-col justify-around items-center pointer-events-none">
                  {Array.from({ length: 7 }).map((_, i) => (
                    <div
                      key={i}
                      className="w-0.5 h-2 rounded-full"
                      style={{ background: `${gilt}66` }}
                    />
                  ))}
                </div>
              </div>

              {/* ========== SAĞ SAYFA ========== */}
              <div
                className="relative flex-1 h-full overflow-hidden"
                style={{
                  background: `
                    radial-gradient(ellipse at 30% 50%, ${paperBg} 0%, ${paperDark} 100%)
                  `,
                }}
              >
                {/* Kağıt dokusu */}
                <div
                  className="absolute inset-0 opacity-55 mix-blend-multiply pointer-events-none"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n2'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.6' numOctaves='3' seed='7'/%3E%3CfeColorMatrix values='0 0 0 0 0.5  0 0 0 0 0.4  0 0 0 0 0.25  0 0 0 0.5 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n2)'/%3E%3C/svg%3E")`,
                  }}
                />
                {/* Foxing */}
                <div
                  className="absolute inset-0 opacity-30 pointer-events-none mix-blend-multiply"
                  style={{
                    backgroundImage: `
                      radial-gradient(circle at 80% 25%, rgba(130,80,30,0.35), transparent 7%),
                      radial-gradient(circle at 20% 60%, rgba(130,80,30,0.25), transparent 5%),
                      radial-gradient(circle at 65% 85%, rgba(130,80,30,0.3), transparent 6%),
                      radial-gradient(circle at 30% 15%, rgba(130,80,30,0.2), transparent 4%)
                    `,
                  }}
                />
                {/* Köşe yıpranması */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: `
                      radial-gradient(circle at 100% 0%, rgba(122,88,50,0.5), transparent 25%),
                      radial-gradient(circle at 100% 100%, rgba(122,88,50,0.55), transparent 22%),
                      radial-gradient(circle at 0% 0%, rgba(122,88,50,0.2), transparent 18%),
                      radial-gradient(circle at 0% 100%, rgba(122,88,50,0.3), transparent 20%)
                    `,
                  }}
                />

                {/* Çerçeveler */}
                <div className="absolute inset-5 pointer-events-none" style={{ border: `0.5px solid ${accentInk}55` }} />
                <div className="absolute inset-[18px] pointer-events-none" style={{ border: `0.5px solid ${accentInk}33` }} />

                {/* Filigran köşeler */}
                <div className="absolute top-3 left-3 pointer-events-none">
                  <FiligreeCorner color={accentInk} />
                </div>
                <div className="absolute top-3 right-3 pointer-events-none" style={{ transform: 'scaleX(-1)' }}>
                  <FiligreeCorner color={accentInk} />
                </div>
                <div className="absolute bottom-3 left-3 pointer-events-none" style={{ transform: 'scaleY(-1)' }}>
                  <FiligreeCorner color={accentInk} />
                </div>
                <div className="absolute bottom-3 right-3 pointer-events-none" style={{ transform: 'scale(-1,-1)' }}>
                  <FiligreeCorner color={accentInk} />
                </div>

                {/* Üst başlık - koleksiyon */}
                <div
                  className="absolute top-9 left-0 right-0 flex flex-col items-center pointer-events-none z-[2]"
                  style={{ color: accentInk }}
                >
                  <OrnamentDivider color={accentInk} />
                  <div className="font-serif italic text-[10px] md:text-[11px] tracking-[0.5em] mt-1">
                    {collection.year || 'MMXXVI'}
                  </div>
                </div>

                {/* İçerik */}
                <AnimatePresence mode="wait" custom={direction}>
                  <motion.div
                    key={page + '-R'}
                    initial={{ rotateY: direction > 0 ? 70 : -70, opacity: 0, filter: 'brightness(0.5)' }}
                    animate={{ rotateY: 0, opacity: 1, filter: 'brightness(1)' }}
                    exit={{ rotateY: direction > 0 ? -70 : 70, opacity: 0, filter: 'brightness(0.5)' }}
                    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    style={{ transformOrigin: 'left center', transformStyle: 'preserve-3d' }}
                    className="absolute inset-0 flex flex-col p-14 md:p-16 pt-20 md:pt-24 pb-14"
                  >
                    {currentPage.type === 'image' ? (
                      /* === Sağda ikinci görsel veya ürün ismi === */
                      rightImg ? (
                        <div className="relative flex-1 flex items-center justify-center">
                          <div
                            className="relative w-[86%] h-[82%] overflow-hidden"
                            style={{
                              boxShadow: `
                                0 8px 30px -8px rgba(0,0,0,0.45),
                                inset 0 0 0 1px ${accentInk}44
                              `,
                              border: `1px solid ${accentInk}aa`,
                              padding: '8px',
                              background: paperBg,
                            }}
                          >
                            <div className="w-full h-full overflow-hidden">
                              <img
                                src={rightImg}
                                alt={product.name}
                                className="w-full h-full object-cover"
                                style={{ filter: 'sepia(0.1) saturate(0.95)' }}
                              />
                            </div>
                          </div>

                          {/* Caption */}
                          <div className="absolute bottom-6 left-0 right-0 text-center">
                            <div className="font-serif italic text-[13px] md:text-[14px]" style={{ color: inkColor, letterSpacing: '0.08em' }}>
                              {product.name}
                            </div>
                            <div className="text-[9px] tracking-[0.4em] mt-1" style={{ color: accentInk, opacity: 0.8 }}>
                              {collection.title.toUpperCase()}
                              {product.prices && Object.keys(product.prices).length > 0 && (
                                <> · <span style={{ color: inkColor, letterSpacing: '0.2em' }}>
                                  {formatPrice(Math.min(...Object.values(product.prices)))}
                                </span></>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex-1 flex items-center justify-center text-center" style={{ color: inkColor }}>
                          <div>
                            <div className="font-serif italic text-4xl mb-2">{product.name}</div>
                            <div className="text-[10px] tracking-[0.4em]" style={{ color: accentInk }}>
                              {collection.title.toUpperCase()}
                            </div>
                          </div>
                        </div>
                      )
                    ) : (
                      /* === Detaylar sayfası === */
                      <div className="flex-1 flex flex-col overflow-hidden">
                        <div
                          className="text-[10px] tracking-[0.5em] mb-3 italic"
                          style={{ color: accentInk, letterSpacing: '0.45em' }}
                        >
                          — Specimen №&nbsp;{String(currentPage.prodIdx + 1).padStart(2, '0')} —
                        </div>

                        <h3
                          className="font-serif text-2xl md:text-[32px] leading-tight mb-2"
                          style={{ color: inkColor, fontWeight: 500 }}
                        >
                          {product.name}
                        </h3>

                        {/* Drop cap ornament */}
                        <div className="flex items-center gap-3 mb-4">
                          <div className="h-px flex-1" style={{ background: `linear-gradient(to right, ${accentInk}99, transparent)` }} />
                          <svg width="22" height="10" viewBox="0 0 22 10" style={{ color: accentInk }}>
                            <path d="M1,5 Q6,0 11,5 Q16,10 21,5" stroke="currentColor" strokeWidth="0.8" fill="none" />
                            <circle cx="11" cy="5" r="1.5" fill="currentColor" />
                          </svg>
                          <div className="h-px flex-1" style={{ background: `linear-gradient(to left, ${accentInk}99, transparent)` }} />
                        </div>

                        <div
                          className="font-serif italic text-[13px] md:text-[14px] leading-relaxed mb-4"
                          style={{ color: inkColor }}
                        >
                          {product.description}
                        </div>

                        <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-[11px] md:text-[12px]" style={{ color: inkColor }}>
                          <div>
                            <div className="text-[9px] tracking-[0.3em] font-sans opacity-60 mb-1">KUMAŞ</div>
                            <div className="italic">{product.material || product.fabric}</div>
                          </div>

                          {product.dimensions && (
                            <div>
                              <div className="text-[9px] tracking-[0.3em] font-sans opacity-60 mb-1">BOYUT</div>
                              <div className="flex flex-wrap gap-1">
                                {(product.sizes || []).map((size) => (
                                  <span
                                    key={size}
                                    className="border px-2 py-0.5 text-[10px] italic"
                                    style={{ borderColor: `${accentInk}aa`, color: inkColor }}
                                  >
                                    {size}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {product.prices && Object.keys(product.prices).length > 0 && (
                            <div className="col-span-2">
                              <div className="text-[9px] tracking-[0.3em] font-sans opacity-60 mb-1">
                                FİYAT ÇİZELGESİ
                              </div>
                              <div className="flex flex-wrap gap-x-5 gap-y-1 italic text-[12px]">
                                {Object.entries(product.prices).map(([sz, pr]) => (
                                  <div key={sz}>
                                    <span className="opacity-70">{sz}</span>
                                    <span className="mx-2">—</span>
                                    <span className="font-semibold not-italic">{formatPrice(pr)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {product.features && product.features.length > 0 && (
                            <div className="col-span-2">
                              <div className="text-[9px] tracking-[0.3em] font-sans opacity-60 mb-1">
                                DETAYLAR
                              </div>
                              <ul className="space-y-0.5 italic text-[11px]">
                                {product.features.slice(0, 4).map((f, fi) => (
                                  <li key={fi} className="flex items-start gap-2">
                                    <span style={{ color: accentInk }}>❧</span>
                                    <span>{f}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>

                        {/* CTA */}
                        <div className="mt-auto pt-5">
                          {product.id ? (
                            <Link
                              to={`/urun/${product.id}`}
                              onClick={onClose}
                              className="group inline-flex items-center gap-3"
                              style={{ color: inkColor }}
                              data-testid="catalog-book-shop-cta"
                            >
                              <span
                                className="font-serif italic text-[13px] border-b pb-0.5 transition-opacity group-hover:opacity-70"
                                style={{ borderColor: inkColor }}
                              >
                                Satın Al & Hikâyeyi Taşı
                              </span>
                              <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" />
                            </Link>
                          ) : (
                            <div className="text-[10px] tracking-[0.3em] italic" style={{ color: accentInk }}>
                              · Yakında · {collection.year} sezonu
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>

                {/* Cilt sırtı tarafındaki sayfa katlama gölgesi */}
                <div
                  className="absolute inset-y-0 left-0 w-16 pointer-events-none"
                  style={{
                    background:
                      'linear-gradient(to left, transparent 0%, rgba(0,0,0,0.08) 40%, rgba(0,0,0,0.4) 100%)',
                  }}
                />

                {/* Sağ sayfa numarası */}
                <div
                  className="absolute bottom-6 left-0 right-0 flex items-center justify-center pointer-events-none"
                  style={{ color: accentInk }}
                >
                  <div className="flex items-center gap-3">
                    <svg width="16" height="6" viewBox="0 0 16 6" style={{ color: accentInk, opacity: 0.6 }}>
                      <path d="M1,3 L15,3 M3,3 Q4,1 5,3 M11,3 Q12,1 13,3" stroke="currentColor" strokeWidth="0.6" fill="none" />
                    </svg>
                    <span className="font-serif italic text-[10px] tracking-[0.35em]">
                      — {page * 2 + 2} —
                    </span>
                    <svg width="16" height="6" viewBox="0 0 16 6" style={{ color: accentInk, opacity: 0.6 }}>
                      <path d="M1,3 L15,3 M3,3 Q4,1 5,3 M11,3 Q12,1 13,3" stroke="currentColor" strokeWidth="0.6" fill="none" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Yaldızlı sayfa kenarları (üst) */}
            <div
              className="absolute left-4 right-4 top-[22px] h-[4px] pointer-events-none"
              style={{
                background:
                  'repeating-linear-gradient(to right, #f6e4b2 0 1px, #d9b670 1px 2px, #a68245 2px 3px)',
                opacity: 0.85,
              }}
            />
            {/* Yaldızlı sayfa kenarları (alt) */}
            <div
              className="absolute left-4 right-4 bottom-[24px] h-[4px] pointer-events-none"
              style={{
                background:
                  'repeating-linear-gradient(to right, #f6e4b2 0 1px, #d9b670 1px 2px, #a68245 2px 3px)',
                opacity: 0.85,
              }}
            />
          </div>

          {/* Nav buttons */}
          <button
            onClick={prev}
            disabled={page === 0}
            className={`absolute left-2 md:-left-20 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full border transition-all backdrop-blur-sm ${
              page === 0
                ? 'opacity-20 cursor-not-allowed border-white/30 text-white/60'
                : 'border-amber-200/40 text-amber-100 hover:bg-amber-100 hover:text-black'
            }`}
            aria-label="Önceki sayfa"
            data-testid="catalog-book-prev"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={next}
            disabled={page === totalPages - 1}
            className={`absolute right-2 md:-right-20 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full border transition-all backdrop-blur-sm ${
              page === totalPages - 1
                ? 'opacity-20 cursor-not-allowed border-white/30 text-white/60'
                : 'border-amber-200/40 text-amber-100 hover:bg-amber-100 hover:text-black'
            }`}
            aria-label="Sonraki sayfa"
            data-testid="catalog-book-next"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Alt — ürün göstergesi */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 max-w-[90vw] overflow-x-auto z-[210]">
          {collection.products.map((_, i) => {
            const isCurrent = pages[page]?.prodIdx === i;
            const firstPageIdxForProduct = pages.findIndex(p => p.prodIdx === i);
            return (
              <button
                key={i}
                onClick={() => {
                  const newIdx = firstPageIdxForProduct >= 0 ? firstPageIdxForProduct : 0;
                  setDirection(newIdx > page ? 1 : -1);
                  setPage(newIdx);
                }}
                className={`transition-all flex-shrink-0 ${
                  isCurrent ? 'w-8 h-1 bg-amber-200' : 'w-2 h-1 bg-amber-200/30 hover:bg-amber-200/60'
                }`}
                aria-label={`Ürün ${i + 1}`}
                data-testid={`catalog-book-dot-${i}`}
              />
            );
          })}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// ============= VINTAGE FLIPBOOK CATALOG END =============

// ============= VINTAGE BOOKSHELF =============
const VintageBookshelf = ({ collections, onOpen }) => {
  const [hoveredIdx, setHoveredIdx] = useState(null);

  return (
    <section
      className="relative py-20 md:py-32 overflow-hidden"
      style={{
        background: 'radial-gradient(ellipse at center top, #2a1a0e 0%, #0f0803 90%)',
      }}
      data-testid="vintage-bookshelf-section"
    >
      {/* Ambient wood grain texture */}
      <div
        className="absolute inset-0 opacity-25 pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 800 300' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='w'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.02 0.5' numOctaves='3' /%3E%3CfeColorMatrix values='0 0 0 0 0.4  0 0 0 0 0.25  0 0 0 0 0.12  0 0 0 1 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23w)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Header */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 mb-14 md:mb-20 text-center">
        <div className="text-xs tracking-[0.5em] text-amber-200/60 mb-4">
          — VALMÉRA KÜTÜPHANESİ —
        </div>
        <h2 className="font-serif text-4xl md:text-6xl font-light text-amber-50 leading-tight">
          Koleksiyon <span className="italic font-thin">Kitapları</span>
        </h2>
        <p className="text-amber-100/50 text-sm md:text-base mt-6 max-w-2xl mx-auto italic">
          Bir kitabı seçin. Kapağı aralayın. Sayfaları arasında ürünlerimizle tanışın.
        </p>
      </div>

      {/* Books row */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-10">
        <div
          className="flex items-end justify-center gap-2 md:gap-3 min-h-[520px] md:min-h-[600px]"
          style={{ perspective: '2500px' }}
        >
          {collections.map((c, i) => {
            const isHovered = hoveredIdx === i;
            const isPushed = hoveredIdx !== null && hoveredIdx !== i;

            return (
              <motion.button
                key={c.slug}
                onClick={() => onOpen(c)}
                onMouseEnter={() => setHoveredIdx(i)}
                onMouseLeave={() => setHoveredIdx(null)}
                initial={{ opacity: 0, y: 80 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.9, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                animate={{
                  y: isHovered ? -40 : 0,
                  rotateY: isHovered ? -18 : (isPushed ? (hoveredIdx > i ? 4 : -4) : 0),
                  scale: isHovered ? 1.05 : 1,
                }}
                style={{
                  transformStyle: 'preserve-3d',
                  transformOrigin: 'center bottom',
                }}
                className="relative group cursor-pointer"
                data-testid={`book-${c.slug}`}
                aria-label={`${c.title} koleksiyon kitabını aç`}
              >
                {/* Book */}
                <div
                  className="relative w-[110px] md:w-[150px] h-[470px] md:h-[560px]"
                  style={{ transformStyle: 'preserve-3d' }}
                >
                  {/* Book back cover (thin depth) */}
                  <div
                    className="absolute inset-0"
                    style={{
                      background: `linear-gradient(135deg, ${c.dark} 0%, #0a0603 100%)`,
                      transform: 'translateZ(-14px)',
                      borderRadius: '2px 8px 8px 2px',
                    }}
                  />

                  {/* Book Spine (front, visible) */}
                  <div
                    className="relative w-full h-full overflow-hidden flex flex-col items-center justify-between py-6"
                    style={{
                      background: `linear-gradient(180deg, ${c.accent}22 0%, ${c.dark} 15%, ${c.dark} 85%, ${c.accent}22 100%)`,
                      borderRadius: '2px 8px 8px 2px',
                      boxShadow: `
                        inset 8px 0 12px -8px rgba(0,0,0,0.7),
                        inset -3px 0 6px -3px rgba(0,0,0,0.5),
                        0 20px 40px -10px rgba(0,0,0,0.6)
                      `,
                      border: `1px solid ${c.accent}55`,
                    }}
                  >
                    {/* Leather texture */}
                    <div
                      className="absolute inset-0 opacity-30 mix-blend-overlay pointer-events-none"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 100 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='l'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='2.5' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23l)' opacity='0.7'/%3E%3C/svg%3E")`,
                      }}
                    />

                    {/* Top gold band */}
                    <div className="relative z-10 w-full flex flex-col items-center pt-2">
                      <div className="w-8 h-0.5" style={{ backgroundColor: c.accent, opacity: 0.8 }} />
                      <div className="w-4 h-0.5 mt-1" style={{ backgroundColor: c.accent, opacity: 0.6 }} />
                    </div>

                    {/* Vertical title */}
                    <div className="relative z-10 flex-1 flex items-center">
                      <div
                        className="font-serif tracking-[0.15em] text-center px-1"
                        style={{
                          writingMode: 'vertical-rl',
                          transform: 'rotate(180deg)',
                          color: c.accent,
                          fontSize: c.title.length > 12 ? '18px' : '22px',
                          fontWeight: 500,
                          textShadow: '0 2px 4px rgba(0,0,0,0.6)',
                          lineHeight: 1,
                        }}
                      >
                        {c.title.toUpperCase()}
                      </div>
                    </div>

                    {/* Ornament and year */}
                    <div className="relative z-10 flex flex-col items-center pb-2 gap-2">
                      <svg width="20" height="14" viewBox="0 0 20 14" style={{ color: c.accent, opacity: 0.7 }}>
                        <path d="M2,7 Q10,2 18,7 Q10,12 2,7 M10,4 v6" stroke="currentColor" strokeWidth="0.6" fill="none" />
                      </svg>
                      <div className="text-[9px] tracking-widest" style={{ color: c.accent, opacity: 0.75 }}>
                        {c.year}
                      </div>
                      <div className="text-[9px] tracking-widest" style={{ color: c.accent, opacity: 0.55 }}>
                        № {c.number}
                      </div>
                      <div className="w-8 h-0.5 mt-1" style={{ backgroundColor: c.accent, opacity: 0.6 }} />
                    </div>
                  </div>

                  {/* Book pages edge (right side) */}
                  <div
                    className="absolute top-1 bottom-1 -right-1 w-1.5"
                    style={{
                      background: 'linear-gradient(to right, #f4ecd8 0%, #d4c298 50%, #a08558 100%)',
                      borderRadius: '0 2px 2px 0',
                      boxShadow: 'inset -1px 0 2px rgba(0,0,0,0.3)',
                    }}
                  />

                  {/* Cover peek (revealed on hover) */}
                  <motion.div
                    className="absolute inset-y-0 right-0 overflow-hidden origin-right"
                    initial={{ scaleX: 0, opacity: 0 }}
                    animate={{
                      scaleX: isHovered ? 1 : 0,
                      opacity: isHovered ? 1 : 0,
                    }}
                    transition={{ duration: 0.5 }}
                    style={{
                      width: '110%',
                      transformOrigin: 'left center',
                      transform: 'translateX(100%)',
                      borderRadius: '0 6px 6px 0',
                      boxShadow: '10px 5px 30px -5px rgba(0,0,0,0.7)',
                    }}
                  >
                    <div
                      className="w-full h-full relative"
                      style={{ backgroundColor: c.dark }}
                    >
                      {c.poster ? (
                        <img src={c.poster} alt={c.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center" style={{
                          background: `linear-gradient(135deg, ${c.dark}, ${c.accent}44)`,
                        }}>
                          <div className="text-white/40 text-xs tracking-[0.3em]">YAKINDA</div>
                        </div>
                      )}
                      <div className="absolute inset-0 pointer-events-none" style={{
                        background: `radial-gradient(circle at 30% 40%, transparent 40%, ${c.dark}88 100%)`
                      }} />
                      {/* Cover title */}
                      <div className="absolute bottom-4 left-4 right-4 text-center">
                        <div className="text-[8px] tracking-[0.4em] text-white/70 mb-1">VALMÉRA</div>
                        <div className="font-serif text-white text-lg italic">{c.title}</div>
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Shadow beneath book */}
                <motion.div
                  className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-24 h-2 rounded-full blur-md"
                  style={{ backgroundColor: '#000' }}
                  animate={{
                    opacity: isHovered ? 0.9 : 0.5,
                    scale: isHovered ? 1.3 : 1,
                  }}
                />

                {/* Hover caption */}
                <motion.div
                  className="absolute left-1/2 -translate-x-1/2 -bottom-14 text-center whitespace-nowrap"
                  animate={{ opacity: isHovered ? 1 : 0, y: isHovered ? 0 : -5 }}
                >
                  <div className="text-xs tracking-[0.3em] text-amber-200">{c.title.toUpperCase()}</div>
                  <div className="text-[9px] tracking-widest text-amber-200/60 italic mt-1">— aç →</div>
                </motion.div>
              </motion.button>
            );
          })}
        </div>

        {/* Wooden shelf plank */}
        <div className="relative mt-4">
          <div
            className="h-4 md:h-6 rounded-sm"
            style={{
              background: 'linear-gradient(180deg, #3a2415 0%, #2a1a0e 50%, #1a0f08 100%)',
              boxShadow: '0 8px 24px -6px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,220,150,0.15)',
            }}
          />
          {/* Shelf front edge */}
          <div className="h-2 -mt-0.5" style={{
            background: 'linear-gradient(180deg, #1a0f08 0%, #050300 100%)',
          }} />
        </div>

        {/* Sub caption */}
        <p className="text-center text-amber-100/40 text-[10px] tracking-[0.4em] mt-8 italic">
          — bir kitap seçin — vintage kataloğu aralayın —
        </p>
      </div>
    </section>
  );
};
// ============= VINTAGE BOOKSHELF END =============


// ============= INTRO HERO =============
const IntroHero = ({ onScrollDown }) => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 0.15], [0, -100]);
  const opacity = useTransform(scrollYProgress, [0, 0.08], [1, 0]);

  return (
    <motion.section
      style={{ y, opacity }}
      className="relative h-screen flex flex-col items-center justify-center bg-[#0a0a0a] text-white overflow-hidden"
    >
      {/* Grain overlay */}
      <div
        className="absolute inset-0 opacity-[0.15] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Vertical text left */}
      <div className="absolute left-8 top-1/2 -translate-y-1/2 rotate-180 tracking-[0.5em] text-[10px] opacity-40" style={{ writingMode: 'vertical-rl' }}>
        VALMÉRA — ATÖLYE ARŞİVİ
      </div>

      {/* Vertical text right */}
      <div className="absolute right-8 top-1/2 -translate-y-1/2 tracking-[0.5em] text-[10px] opacity-40" style={{ writingMode: 'vertical-rl' }}>
        2025 — FEBRUARY EDITION
      </div>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
        className="text-center px-6 max-w-5xl"
      >
        <div className="text-xs tracking-[0.6em] opacity-70 mb-8">CANLI KATALOG</div>
        <h1 className="font-serif text-6xl md:text-8xl lg:text-9xl font-light leading-[0.95] tracking-tight mb-8">
          <span className="italic font-thin">Beş</span> hikâye,<br />
          bir <span className="italic font-thin">iplik</span>.
        </h1>
        <p className="text-base md:text-lg opacity-70 max-w-xl mx-auto leading-relaxed font-light">
          VALMÉRA&apos;nın 2025 yılına damgasını vuran beş koleksiyonu — her biri farklı bir dokunuş, farklı bir hafıza.
        </p>
      </motion.div>

      {/* Scroll indicator */}
      <motion.button
        onClick={onScrollDown}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 10, 0] }}
        transition={{ opacity: { delay: 1.5, duration: 0.6 }, y: { repeat: Infinity, duration: 2, ease: 'easeInOut' } }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-xs tracking-[0.3em] opacity-70 hover:opacity-100 transition-opacity"
        data-testid="scroll-indicator"
      >
        KAYDIRIN
        <ChevronDown size={16} />
      </motion.button>
    </motion.section>
  );
};

// ============= COLLECTION CARD (BIG POSTER) =============
const CollectionPoster = ({ collection, index, onOpen }) => {
  const cardRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ['start end', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], [80, -80]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.95, 1, 0.95]);

  const isReversed = index % 2 === 1;

  return (
    <motion.section
      ref={cardRef}
      style={{ backgroundColor: collection.dark }}
      className="relative min-h-screen py-24 overflow-hidden"
      data-testid={`collection-section-${collection.slug}`}
    >
      {/* Subtle grain */}
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        }}
      />

      <div className={`max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-8 lg:gap-16 items-center h-full ${isReversed ? 'lg:[direction:rtl]' : ''}`}>
        {/* Poster / Image side */}
        <motion.div
          style={{ y, scale }}
          className="lg:col-span-7 [direction:ltr]"
        >
          <div className="relative group cursor-pointer" onClick={() => onOpen(collection)}>
            {/* Number badge */}
            <div
              className="absolute -top-6 md:-top-10 -left-2 md:-left-6 z-20 font-serif text-[80px] md:text-[160px] leading-none font-thin italic opacity-90"
              style={{ color: collection.accent }}
            >
              {collection.number}
            </div>

            {/* Poster or placeholder */}
            <div className="relative aspect-[3/4] overflow-hidden shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)]">
              {collection.poster ? (
                <motion.img
                  src={collection.poster}
                  alt={collection.title}
                  className="w-full h-full object-cover"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                />
              ) : (
                <div
                  className="w-full h-full flex flex-col items-center justify-center relative overflow-hidden"
                  style={{
                    background: `linear-gradient(135deg, ${collection.dark} 0%, ${collection.accent}44 50%, ${collection.dark} 100%)`,
                  }}
                >
                  {/* Ornamental placeholder */}
                  <svg viewBox="0 0 200 200" className="w-1/3 h-1/3 opacity-30" style={{ color: collection.accent }}>
                    <circle cx="100" cy="100" r="60" fill="none" stroke="currentColor" strokeWidth="0.5" />
                    <circle cx="100" cy="100" r="40" fill="none" stroke="currentColor" strokeWidth="0.5" />
                    <circle cx="100" cy="100" r="20" fill="none" stroke="currentColor" strokeWidth="0.5" />
                    <path d="M100,20 L100,180 M20,100 L180,100 M40,40 L160,160 M40,160 L160,40" stroke="currentColor" strokeWidth="0.3" fill="none" />
                  </svg>
                  <div className="mt-6 text-white/40 text-xs tracking-[0.4em]">YAKINDA</div>
                </div>
              )}

              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-700 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform translate-y-4 group-hover:translate-y-0 border border-white/60 px-8 py-3 text-white text-xs tracking-[0.3em] backdrop-blur-sm">
                  {collection.available ? 'KOLEKSİYONU AÇ' : 'KATALOĞU GÖR'}
                </div>
              </div>
            </div>

            {/* Corner marks */}
            <div className="absolute top-2 right-2 w-6 h-6 border-t-2 border-r-2" style={{ borderColor: collection.accent }} />
            <div className="absolute bottom-2 left-2 w-6 h-6 border-b-2 border-l-2" style={{ borderColor: collection.accent }} />
          </div>
        </motion.div>

        {/* Text side */}
        <motion.div
          initial={{ opacity: 0, x: isReversed ? -50 : 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 text-white [direction:ltr]"
        >
          <div className="text-xs tracking-[0.4em] mb-4" style={{ color: collection.accent }}>
            {collection.year} · KOLEKSİYON {collection.number}
          </div>
          <h2 className="font-serif text-5xl md:text-6xl font-light mb-6 leading-tight">
            {collection.title}
          </h2>
          <p className="text-white/70 text-base md:text-lg italic font-light mb-8 leading-relaxed">
            {collection.subtitle}
          </p>
          <div className="w-16 h-px mb-6" style={{ backgroundColor: collection.accent }} />
          <p className="text-white/60 text-sm leading-relaxed mb-10 max-w-md">
            {collection.tagline}
          </p>

          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => onOpen(collection)}
              className="group inline-flex items-center gap-3 px-8 py-4 border border-white/40 text-white text-xs tracking-[0.3em] hover:bg-white hover:text-black transition-all duration-500"
              data-testid={`open-collection-${collection.slug}`}
            >
              {collection.available ? 'KEŞFET' : 'KATALOĞU İNCELE'}
              <ArrowUpRight size={16} className="transition-transform group-hover:rotate-45" />
            </button>

            {collection.available && (
              <Link
                to={`/urunler?collection=${collection.slug}`}
                className="inline-flex items-center gap-3 px-8 py-4 text-white/70 text-xs tracking-[0.3em] hover:text-white transition-colors"
                data-testid={`shop-collection-${collection.slug}`}
              >
                ÜRÜNLERE GİT
                <ArrowRight size={14} />
              </Link>
            )}

            {!collection.available && (
              <div className="inline-flex items-center gap-3 px-8 py-4 text-white/40 text-xs tracking-[0.3em]">
                · YAKINDA
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

// ============= COLLECTION MODAL / DEEP VIEW =============
const CollectionDetailModal = ({ collection, onClose }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [bookOpenIndex, setBookOpenIndex] = useState(null);
  const [cinematicIndex, setCinematicIndex] = useState(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) videoRef.current.pause();
      else videoRef.current.play();
      setIsPlaying(!isPlaying);
    }
  };

  if (!collection) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
        className="fixed inset-0 z-[100] overflow-y-auto"
        style={{ backgroundColor: collection.dark }}
        data-testid="collection-detail-modal"
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="fixed top-6 right-6 md:top-8 md:right-8 z-[110] w-12 h-12 border border-white/40 text-white flex items-center justify-center hover:bg-white hover:text-black transition-all"
          aria-label="Kapat"
          data-testid="close-collection-detail"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Hero */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="relative h-screen flex items-end overflow-hidden"
        >
          {collection.chapters && collection.chapters.length > 1 ? (
            <HeroAutoSlider chapters={collection.chapters} />
          ) : collection.video ? (
            <video
              ref={videoRef}
              src={collection.video}
              poster={collection.poster || undefined}
              autoPlay
              loop
              muted
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : collection.poster ? (
            <img
              src={collection.poster}
              alt={collection.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : null}

          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/40" />

          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 pb-20 text-white w-full"
          >
            <div className="text-xs tracking-[0.5em] mb-6" style={{ color: collection.accent }}>
              KOLEKSİYON {collection.number} · {collection.year}
            </div>
            <h1 className="font-serif text-6xl md:text-8xl lg:text-9xl font-light leading-none mb-6">
              {collection.title}
            </h1>
            <p className="text-lg md:text-xl italic opacity-80 max-w-2xl mb-10 font-light">
              {collection.subtitle}
            </p>

            <div className="flex flex-wrap items-center gap-6">
              <Link
                to={`/urunler?collection=${collection.slug}`}
                className="inline-flex items-center gap-3 bg-white text-black px-8 py-4 text-xs tracking-[0.3em] hover:bg-white/90 transition-all"
                data-testid="modal-shop-collection"
              >
                ÜRÜNLERE GİT
                <ArrowRight size={14} />
              </Link>

              {collection.video && (
                <button
                  onClick={togglePlay}
                  className="inline-flex items-center gap-3 border border-white/40 text-white px-6 py-4 text-xs tracking-[0.3em] hover:bg-white/10 transition-all"
                >
                  {isPlaying ? <Pause size={14} /> : <Play size={14} />}
                  {isPlaying ? 'DURAKLAT' : 'OYNAT'}
                </button>
              )}
            </div>
          </motion.div>
        </motion.section>

        {/* Story */}
        <section className="py-24 md:py-32 px-6 md:px-10">
          <div className="max-w-3xl mx-auto text-white">
            <div className="text-xs tracking-[0.4em] mb-4 opacity-60" style={{ color: collection.accent }}>
              — HİKÂYE
            </div>
            <h2 className="font-serif text-4xl md:text-6xl font-light mb-12 leading-tight">
              {collection.title}
            </h2>
            <div className="space-y-8 text-lg md:text-xl leading-relaxed font-light">
              {collection.story.map((paragraph, i) => (
                <motion.p
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: i * 0.15 }}
                  className="text-white/80"
                >
                  {paragraph}
                </motion.p>
              ))}
            </div>

            {collection.quote && (
              <motion.blockquote
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1 }}
                className="mt-16 text-2xl md:text-3xl italic font-light text-center py-12 border-y border-white/20"
                style={{ color: collection.accent }}
              >
                &ldquo;{collection.quote}&rdquo;
              </motion.blockquote>
            )}
          </div>
        </section>

        {/* Chapters / Visual Journey */}
        {collection.chapters.length > 0 && (
          <section className="py-24 px-6 md:px-10 bg-black/30">
            <div className="max-w-6xl mx-auto">
              <div className="text-xs tracking-[0.4em] mb-12 opacity-60 text-white text-center" style={{ color: collection.accent }}>
                — GÖRSEL YOLCULUK
              </div>
              <div className="grid md:grid-cols-3 gap-6 md:gap-10">
                {collection.chapters.map((chapter, i) => (
                  <motion.button
                    key={i}
                    type="button"
                    onClick={() => setCinematicIndex(i)}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: i * 0.15 }}
                    className="group text-left cursor-pointer"
                    data-testid={`chapter-image-${collection.slug}-${i}`}
                    aria-label={`${chapter.title} sinematik gösterimini aç`}
                  >
                    <div className="relative overflow-hidden aspect-[3/4] mb-6">
                      <img
                        src={chapter.image}
                        alt={chapter.title}
                        className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                      />
                      {/* Play button overlay */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-500 flex items-center justify-center">
                        <div className="w-16 h-16 md:w-20 md:h-20 rounded-full border-2 flex items-center justify-center opacity-0 group-hover:opacity-100 backdrop-blur-sm transition-all duration-500 transform translate-y-4 group-hover:translate-y-0" style={{ borderColor: collection.accent, backgroundColor: 'rgba(0,0,0,0.4)' }}>
                          <Play size={22} className="text-white ml-1" fill="currentColor" />
                        </div>
                      </div>
                      {/* Live badge */}
                      <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-black/60 backdrop-blur-sm px-2 py-1">
                        <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: collection.accent }} />
                        <span className="text-[9px] tracking-[0.3em] text-white">CANLI</span>
                      </div>
                    </div>
                    <div className="text-xs tracking-[0.4em] mb-3" style={{ color: collection.accent }}>
                      {String(i + 1).padStart(2, '0')} · {chapter.title}
                    </div>
                    <p className="text-white/70 text-sm leading-relaxed">{chapter.desc}</p>
                  </motion.button>
                ))}
              </div>
              <p className="text-center text-white/40 text-[10px] tracking-[0.4em] mt-8">
                — GÖRSELLERE TIKLAYIN — SİNEMATİK MODA GEÇİN —
              </p>
            </div>
          </section>
        )}

        {/* Gallery */}
        {collection.gallery.length > 0 && (
          <section className="py-24 px-6 md:px-10">
            <div className="max-w-6xl mx-auto">
              <div className="text-xs tracking-[0.4em] mb-12 opacity-60 text-white text-center" style={{ color: collection.accent }}>
                — GALERİ
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                {collection.gallery.map((img, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, delay: i * 0.1 }}
                    className={i % 3 === 0 ? 'md:col-span-2' : ''}
                  >
                    <img src={img} alt="" className="w-full aspect-video object-cover" />
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Product Catalog */}
        {collection.products && collection.products.length > 0 && (
          <section className="py-24 px-6 md:px-10 bg-black/40">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <div className="text-xs tracking-[0.4em] mb-4 opacity-60" style={{ color: collection.accent }}>
                  — KOLEKSİYON KATALOĞU
                </div>
                <h3 className="font-serif text-3xl md:text-5xl font-light text-white">
                  Ürünler
                </h3>
                <p className="text-white/50 text-sm mt-4 max-w-md mx-auto">
                  {collection.available ? 'Boyutlara ve stillere göre koleksiyon parçaları.' : 'Koleksiyonda yer alacak parçaların ön izlemesi.'}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {collection.products.map((product, i) => (
                  <motion.button
                    key={i}
                    onClick={() => setBookOpenIndex(i)}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-50px' }}
                    transition={{ duration: 0.7, delay: (i % 3) * 0.1 }}
                    className="group relative text-left cursor-pointer"
                    data-testid={`product-card-${collection.slug}-${i}`}
                    aria-label={`${product.name} kataloğunu aç`}
                  >
                    {/* Card */}
                    <div
                      className="relative aspect-[4/5] overflow-hidden border transition-all duration-500 group-hover:border-opacity-100"
                      style={{ borderColor: `${collection.accent}44`, backgroundColor: `${collection.dark}` }}
                    >
                      {product.image ? (
                        // Real product image if uploaded
                        <>
                          <img
                            src={product.image}
                            alt={product.name}
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 pointer-events-none" style={{
                            background: `linear-gradient(to top, ${collection.dark}dd 0%, transparent 40%, transparent 100%)`,
                          }} />
                        </>
                      ) : (
                        <>
                          {/* Ornamental background (placeholder) */}
                          <div
                            className="absolute inset-0 opacity-20"
                            style={{
                              background: `radial-gradient(circle at 50% 30%, ${collection.accent}55 0%, transparent 60%)`,
                            }}
                          />
                          {/* Placeholder ornament */}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <svg viewBox="0 0 200 200" className="w-2/5 h-2/5 opacity-30 group-hover:opacity-60 group-hover:rotate-45 transition-all duration-1000" style={{ color: collection.accent }}>
                              <circle cx="100" cy="100" r="70" fill="none" stroke="currentColor" strokeWidth="0.5" />
                              <circle cx="100" cy="100" r="45" fill="none" stroke="currentColor" strokeWidth="0.5" />
                              <path d="M100,30 L110,90 L170,100 L110,110 L100,170 L90,110 L30,100 L90,90 Z" fill="currentColor" opacity="0.4" />
                            </svg>
                          </div>
                        </>
                      )}

                      {/* Corner marks */}
                      <div className="absolute top-3 right-3 w-4 h-4 border-t border-r opacity-60" style={{ borderColor: collection.accent }} />
                      <div className="absolute bottom-3 left-3 w-4 h-4 border-b border-l opacity-60" style={{ borderColor: collection.accent }} />

                      {/* Number */}
                      <div className="absolute top-4 left-4 text-white/40 text-[10px] tracking-[0.3em]">
                        {String(i + 1).padStart(2, '0')} / {String(collection.products.length).padStart(2, '0')}
                      </div>

                      {/* "Yakında" badge */}
                      {!collection.available && (
                        <div className="absolute bottom-4 right-4 text-white/60 text-[9px] tracking-[0.3em] border border-white/30 px-2 py-1">
                          YAKINDA
                        </div>
                      )}

                      {/* Content overlay bottom */}
                      <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/80 to-transparent pt-16">
                        <h4 className="font-serif text-xl text-white mb-2 leading-tight">
                          {product.name}
                        </h4>
                        <div className="text-[10px] text-white/50 tracking-[0.2em] uppercase mb-2">
                          {product.fabric}
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {product.sizes.map((size) => (
                            <span
                              key={size}
                              className="text-[10px] tracking-wider px-2 py-0.5 border"
                              style={{ borderColor: `${collection.accent}88`, color: collection.accent }}
                            >
                              {size}
                            </span>
                          ))}
                        </div>
                        {product.styles && product.styles.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {product.styles.map((style) => (
                              <span
                                key={style}
                                className="text-[9px] tracking-wider text-white/60 uppercase"
                              >
                                · {style}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>

              {/* Book helper hint */}
              <p className="text-center text-white/40 text-[10px] tracking-[0.4em] mt-8">
                — HERHANGİ BİR KARTA TIKLAYIN — VINTAGE KATALOĞU AÇIN —
              </p>
            </div>
          </section>
        )}

        {/* CTA Footer */}
        <section className="py-24 px-6 md:px-10 text-center">
          <div className="max-w-2xl mx-auto text-white">
            <h3 className="font-serif text-3xl md:text-5xl font-light mb-6">
              {collection.available ? 'Kendine bir hikâye seç.' : 'Yakında sende olacak.'}
            </h3>
            <p className="text-white/60 mb-10">
              {collection.available
                ? `${collection.title} koleksiyonunun ürünlerini keşfet.`
                : `${collection.title} koleksiyonu çok yakında ${collection.year}'da.`}
            </p>
            {collection.available ? (
              <Link
                to={`/urunler?collection=${collection.slug}`}
                className="inline-flex items-center gap-3 bg-white text-black px-10 py-4 text-xs tracking-[0.3em] hover:bg-white/90 transition-all"
                data-testid="modal-cta-shop"
              >
                KOLEKSİYONU İNCELE
                <ArrowRight size={16} />
              </Link>
            ) : (
              <div
                className="inline-flex items-center gap-3 border border-white/40 text-white px-10 py-4 text-xs tracking-[0.3em]"
                data-testid="modal-cta-coming"
              >
                YAKINDA
              </div>
            )}
          </div>
        </section>
      </motion.div>

      {/* Vintage Catalog Book */}
      {bookOpenIndex !== null && (
        <VintageCatalogBook
          collection={collection}
          startIndex={bookOpenIndex}
          onClose={() => setBookOpenIndex(null)}
        />
      )}

      {/* Cinematic Chapter Viewer */}
      {cinematicIndex !== null && collection.chapters.length > 0 && (
        <CinematicImageViewer
          chapters={collection.chapters}
          startIndex={cinematicIndex}
          accent={collection.accent}
          onClose={() => setCinematicIndex(null)}
        />
      )}
    </AnimatePresence>
  );
};

// ============= MAIN PAGE =============
const CollectionsPage = () => {
  const { slug } = useParams();
  const [activeCollection, setActiveCollection] = useState(null);
  const [mergedCollections, setMergedCollections] = useState(collections);
  const collectionsRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Fetch admin-uploaded assets from backend and merge with defaults
  useEffect(() => {
    const load = async () => {
      try {
        const [assetsRes, productsRes] = await Promise.all([
          fetch(`${backendUrl}/api/collections/assets`),
          fetch(`${backendUrl}/api/products`),
        ]);
        const assetsMap = assetsRes.ok ? await assetsRes.json() : {};
        const allProducts = productsRes.ok ? await productsRes.json() : [];

        // Group backend products by their collection slug
        const productsBySlug = {};
        allProducts.forEach((p) => {
          const slug = p.collection || 'kokten-isiga';
          if (!productsBySlug[slug]) productsBySlug[slug] = [];
          productsBySlug[slug].push({
            id: p.id,
            name: p.name,
            altName: p.altName,
            sizes: p.sizes || ['65x65'],
            fabric: p.material || '%100 İpek',
            material: p.material,
            dimensions: p.dimensions,
            description: p.description,
            features: p.features || [],
            prices: p.prices || {},
            styles: [],
            image: (p.images && p.images[0]) || p.image || null,
            images: p.images || (p.image ? [p.image] : []),
          });
        });

        const merged = collections.map((c) => {
          const assets = assetsMap[c.slug];
          const backendProducts = productsBySlug[c.slug] || [];
          const nextChapters = (assets && assets.chapter_images && assets.chapter_images.length > 0 && c.chapters.length > 0)
            ? c.chapters.map((ch, i) => ({ ...ch, image: assets.chapter_images[i] || ch.image }))
            : c.chapters;
          // If there are real DB products for this slug, use them (with images). Otherwise fallback to hardcoded defaults.
          const finalProducts = backendProducts.length > 0 ? backendProducts : c.products;
          return {
            ...c,
            poster: (assets && assets.poster) || c.poster,
            video: (assets && assets.hero_video) || c.video,
            gallery: (assets && assets.gallery && assets.gallery.length > 0) ? assets.gallery : c.gallery,
            chapters: nextChapters,
            products: finalProducts,
          };
        });
        setMergedCollections(merged);
      } catch (err) {
        console.error('Collection assets/products fetch failed:', err);
      }
    };
    load();
  }, [backendUrl]);

  // Auto-open collection if URL has slug (e.g. /koleksiyonlar/kokten-isiga)
  useEffect(() => {
    if (slug) {
      const found = mergedCollections.find((c) => c.slug === slug);
      if (found) setActiveCollection(found);
    }
  }, [slug, mergedCollections]);

  const scrollToCollections = () => {
    collectionsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-[#0a0a0a] -mt-20" data-testid="collections-page">
      {/* Bookshelf first */}
      <div ref={collectionsRef} className="pt-24">
        <VintageBookshelf collections={mergedCollections} onOpen={setActiveCollection} />
      </div>

      {/* Intro at the bottom now (kept for brand storytelling) */}
      <IntroHero onScrollDown={scrollToCollections} />

      {/* Closing */}
      <section className="py-32 bg-[#0a0a0a] text-center text-white px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
        >
          <div className="text-xs tracking-[0.5em] opacity-40 mb-8">— VALMÉRA</div>
          <h3 className="font-serif text-4xl md:text-6xl font-light italic max-w-4xl mx-auto leading-tight">
            &ldquo;İpek, bir hafızayı taşımanın en zarif yoludur.&rdquo;
          </h3>
        </motion.div>
      </section>

      {/* Detail Modal */}
      {activeCollection && (
        <CollectionDetailModal
          collection={activeCollection}
          onClose={() => setActiveCollection(null)}
        />
      )}
    </div>
  );
};

export default CollectionsPage;
